import { Backdrop, Box, ListItemIcon, ListItemText, MenuItem, MenuList, Popper, Typography } from "@mui/material";
import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import { PopperPlacementType } from "@mui/material/Popper/BasePopper.types";
import { StyledFilterPaper } from "@common/components/filterPopover/FilterPopover.tsx";
import { filterPopoverSubMenuWidth } from "@common/components/filterPopover/FilterPopoverSubMenu.tsx";
import { FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { FilterOperatorUi } from "@common/api/filterTypes.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";

export interface FilterBarValueOperatorMenuProps {
    open: boolean;
    submenuAnchorRef: HTMLElement | undefined;
    filterOptionType: FilterOptionType;
    filterOperator: FilterOperatorUi | undefined;
    onChange: (operator: FilterOperatorUi) => void;
    onBackdropClick?: () => void;
    placement?: PopperPlacementType;
}

function FilterBarValueOperatorMenu(props: FilterBarValueOperatorMenuProps) {
    const { open, submenuAnchorRef, filterOptionType, filterOperator, onChange, onBackdropClick, placement } = props;
    const { translateString } = useTranslation();

    const currentOperator = filterOperator ?? FilterOperatorUi.Positive;
    const labels = getLabelsForType(filterOptionType, translateString);

    return (
        <>
            <Backdrop open={open && onBackdropClick !== undefined} sx={{ zIndex: 1200 }} invisible onClick={() => onBackdropClick?.()} />
            <Popper
                open={open}
                anchorEl={submenuAnchorRef}
                placement={placement ?? "bottom-start"}
                sx={{ minWidth: filterPopoverSubMenuWidth, zIndex: 1300 }}
                modifiers={[
                    { name: "offset", options: { offset: [0, 2] } },
                    { name: "flip", enabled: false },
                ]}
            >
                <StyledFilterPaper sx={{ maxHeight: "none", padding: 0, paddingRight: 0, marginTop: 0 }}>
                    <MenuList role="listbox" aria-label="Filter operator" sx={{ p: 0 }}>
                        {renderOption(FilterOperatorUi.Positive, labels.positive, currentOperator === FilterOperatorUi.Positive, onChange)}
                        {renderOption(FilterOperatorUi.Negative, labels.negative, currentOperator === FilterOperatorUi.Negative, onChange)}
                    </MenuList>
                </StyledFilterPaper>
            </Popper>
        </>
    );
}

function renderOption(operator: FilterOperatorUi, label: string, isSelected: boolean, onChange: (op: FilterOperatorUi) => void) {
    return (
        <MenuItem
            role="option"
            aria-selected={isSelected}
            selected={isSelected}
            onClick={() => onChange(operator)}
            sx={{ fontSize: "14px", px: "12px", "& .MuiListItemIcon-root": { minWidth: 24 } }}
        >
            <ListItemIcon sx={{ minWidth: 24, visibility: isSelected ? "visible" : "hidden" }}>
                <CheckIcon fontSize="small" sx={{ fontSize: "16px" }} />
            </ListItemIcon>
            <ListItemText
                primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                            {label}
                        </Typography>
                    </Box>
                }
            />
        </MenuItem>
    );
}

function getLabelsForType(type: FilterOptionType, translateString: (key: string) => string): { positive: string; negative: string } {
    if (type === FilterOptionType.TextField) {
        return {
            positive: translateString("filterOperatorContain"),
            negative: translateString("filterOperatorNotContain"),
        };
    }

    return {
        positive: translateString("filterOperatorAnyOf"),
        negative: translateString("filterOperatorNoneOf"),
    };
}

export default FilterBarValueOperatorMenu;
